import React from 'react';
import { Card, Group, Loader, Progress, Stack, Text, Avatar, ThemeIcon } from '@mantine/core';
import { IconCheck, IconLoader, IconMinus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCycleProgress } from '../../hooks/useApi';
import { User } from '../../types';

interface ProgressDashboardProps {
  cycleId: string;
  subjects: User[];
}

export const ProgressDashboard: React.FC<ProgressDashboardProps> = ({ cycleId, subjects }) => {
  const { t } = useTranslation();
  const { data: progress, isLoading } = useCycleProgress(cycleId);

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!progress) return null;

  // Build a name map from subjects
  const subjectMap = new Map(subjects.map((s) => [s._id, s]));

  return (
    <Stack gap="md">
      {/* Overall progress card */}
      <Card withBorder radius="md" p="lg">
        <Stack gap="sm">
          <Text fw={600} size="lg">
            {t('review360.overallProgress', {
              submitted: progress.submitted,
              total: progress.total,
              percentage: progress.percentage,
            })}
          </Text>
          <Progress value={progress.percentage} color="teal" size="lg" radius="md" />
        </Stack>
      </Card>

      {/* Per-subject breakdown */}
      <Stack gap="sm">
        {Object.entries(progress.bySubject).map(([subjectId, data]) => {
          const subject = subjectMap.get(subjectId);
          const subjectPercentage =
            data.total > 0 ? Math.round((data.submitted / data.total) * 100) : 0;

          const isAllDone = data.total > 0 && data.submitted === data.total;
          const isInProgress = data.submitted > 0 && data.submitted < data.total;

          let statusIcon: React.ReactNode;
          if (isAllDone) {
            statusIcon = (
              <ThemeIcon color="teal" variant="light" size="sm" radius="xl">
                <IconCheck size={14} />
              </ThemeIcon>
            );
          } else if (isInProgress) {
            statusIcon = (
              <ThemeIcon color="blue" variant="light" size="sm" radius="xl">
                <IconLoader size={14} />
              </ThemeIcon>
            );
          } else {
            statusIcon = (
              <ThemeIcon color="gray" variant="light" size="sm" radius="xl">
                <IconMinus size={14} />
              </ThemeIcon>
            );
          }

          return (
            <Card key={subjectId} withBorder radius="md" p="sm">
              <Group gap="sm" wrap="nowrap">
                <Avatar src={subject?.avatar} radius="xl" size="sm">
                  {subject?.name?.charAt(0) ?? '?'}
                </Avatar>
                <Text size="sm" fw={500} style={{ minWidth: 120 }} truncate>
                  {subject?.name ?? subjectId}
                </Text>
                <Text size="sm" c="dimmed" style={{ minWidth: 40 }}>
                  {data.submitted}/{data.total}
                </Text>
                <Progress
                  value={subjectPercentage}
                  color="teal"
                  size="sm"
                  radius="md"
                  style={{ flex: 1 }}
                />
                {statusIcon}
              </Group>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );
};
